import { motion, useReducedMotion, type HTMLMotionProps } from "framer-motion";
import type { ComponentProps } from "react";
import { cn } from "@/lib/cn";

type Variant = "subtle" | "solid";

const base =
  "inline-flex items-center justify-center rounded-full border px-3.5 py-2 text-[13px] font-medium";

const variants: Record<Variant, string> = {
  subtle: "border-border/10 bg-panel/10 text-fg/85",
  solid: "border-accent2/22 bg-panel/14 text-fg/90"
};

export type PillProps = ComponentProps<"span"> & {
  variant?: Variant;
};

export function Pill({ variant = "subtle", className, ...props }: PillProps) {
  return <span className={cn(base, variants[variant], className)} {...props} />;
}

export type PillButtonProps = HTMLMotionProps<"button"> & {
  active?: boolean;
};

export function PillButton({ active, className, ...props }: PillButtonProps) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.button
      type="button"
      whileTap={reduceMotion ? undefined : { scale: 0.98 }}
      className={cn(
        "inline-flex items-center justify-center rounded-full border px-3.5 py-2 text-[13px] font-semibold",
        active
          ? "border-accent2/28 bg-panel/18 text-fg"
          : "border-border/10 bg-panel/8 text-fg/80 hover:bg-panel/12",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/55",
        "transition",
        className
      )}
      {...props}
    />
  );
}

